/**
 * Unified Analytics Service — compat shim
 *
 * This file is kept for backward compatibility with existing call sites that
 * import from 'analyticsService'. New code should import directly from
 * 'analytics' (the typed facade).
 *
 * The shim forwards all calls to the analytics facade so there is a single
 * fan-out path to Mixpanel, GA4, PostHog, and the server relay.
 */

import { analytics } from './analytics';
import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('ANALYTICS_SERVICE');

export interface UnifiedAnalyticsEvent {
  name: string;
  category?: string;
  properties?: Record<string, any>;
  value?: number;
}

export interface UnifiedPageView {
  page_title?: string;
  page_path?: string;
  properties?: Record<string, any>;
}

export interface UnifiedUserProperties {
  user_id?: string;
  properties?: Record<string, any>;
}

class UnifiedAnalyticsService {
  track(event: UnifiedAnalyticsEvent): void {
    try {
      // Forward through the typed facade. Events not in the catalog are still
      // passed through — the facade logs a warning but does not drop them.
      analytics.track(event.name as any, {
        ...(event.properties ?? {}),
        ...(event.category && { category: event.category }),
        ...(event.value !== undefined && { value: event.value }),
      });
    } catch (err) {
      log.warn('analyticsService.track error:', err);
    }
  }

  trackPageView(pageView: UnifiedPageView): void {
    analytics.page(pageView.page_path ?? window.location.pathname, {
      page_title: pageView.page_title,
      ...pageView.properties,
    });
  }

  setUserProperties(userProperties: UnifiedUserProperties): void {
    if (userProperties.user_id) {
      analytics.identify(userProperties.user_id, userProperties.properties ?? {});
    }
  }

  trackLogin(method: string, userId?: string): void {
    this.track({ name: 'login', category: 'engagement', properties: { method, user_id: userId } });
  }

  trackSignup(method: string, userId?: string, additionalProperties?: Record<string, any>): void {
    analytics.track('signup_completed', {
      method,
      invited: false,
      ...additionalProperties,
    });
  }

  trackAppUsage(appName: string, action: string, additionalData?: Record<string, any>): void {
    this.track({ name: `app_${action}`, category: 'app_usage', properties: { app_name: appName, action, ...additionalData } });
  }

  trackSearch(_searchTerm: string, searchType: string, resultCount?: number): void {
    log.debug('Search tracking disabled:', { searchType, resultCount });
  }

  trackError(errorMessage: string, errorType: string, additionalData?: Record<string, any>): void {
    analytics.track('error_shown', { error_type: errorType, message: errorMessage, ...additionalData });
  }

  trackFeatureUsage(featureName: string, action: string, _additionalData?: Record<string, any>): void {
    log.debug('trackFeatureUsage forwarded to analytics.track:', featureName, action);
  }

  trackBusinessEvent(eventName: string, value?: number, additionalData?: Record<string, any>): void {
    this.track({ name: eventName, value, properties: additionalData });
  }

  isReady(): { mixpanel: boolean; ga4: boolean } {
    return { mixpanel: true, ga4: true };
  }

  async flushEvents(): Promise<void> {
    await analytics.flush();
  }

  trackFunnelStep(stepName: string, properties?: Record<string, any>): void {
    this.track({ name: stepName, category: 'signup_funnel', properties });
  }
}

const analyticsService = new UnifiedAnalyticsService();
export default analyticsService;
